import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useParams, Link,useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { projectsData } from './projectsData';

const ProjectDetailsSection = styled(motion.section)`
  min-height: 100vh;
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.background};
  position: relative;
`;

const DetailsContainer = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const BackButton = styled(motion(Link))`
  position: fixed;
  top: 2rem;
  left: 2rem;
  padding: 0.8rem 1.5rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  z-index: 10;
  box-shadow: ${({ theme }) => theme.shadowMd};
  transition: transform 0.3s, background 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadowLg};
    background: ${({ theme }) => theme.primary}; // Change background color on hover
  }
`;

const ProjectHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: clamp(2.5rem, 5vw, 4rem);
    margin-bottom: 1rem;
    background: ${({ theme }) => theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: fadeIn 0.5s ease forwards; // Added animation
  }

  .subtitle {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.textSecondary};
    max-width: 700px;
    margin: 0 auto;
  }
`;

const ProjectImage = styled(motion.div)`
  width: 100%;
  height: 500px;
  border-radius: 24px;
  overflow: hidden;
  margin-bottom: 4rem;
  box-shadow: ${({ theme }) => theme.shadowXl};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
    
    &:hover {
      transform: scale(1.05); // Scale effect on hover
    }
  }
`;

const ContentSection = styled(motion.div)`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.text};
  }

  p {
    color: ${({ theme }) => theme.textSecondary};
    line-height: 1.8;
    margin-bottom: 2rem;
  }
`;

const Sidebar = styled.div`
  background: ${({ theme }) => theme.card};
  padding: 2rem;
  border-radius: 20px;
  position: sticky;
  top: 2rem;
  height: fit-content;
  box-shadow: ${({ theme }) => theme.shadowLg};
`;

const TechList = styled.div`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.text};
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.8rem;
      color: ${({ theme }) => theme.textSecondary};

      &::before {
        content: "•";
        color: ${({ theme }) => theme.primary};
      }
    }
  }
`;



const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 4rem;

  .image-container {
    border-radius: 16px;
    overflow: hidden;
    height: 250px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }
  }
`;

const ProjectDetails = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const loadProject = () => {
        try {
          setIsLoading(true);
          const foundProject = projectsData.find(p => p.id === parseInt(id));
          
          if (!foundProject) {
            setError('Project not found');
            return;
          }
  
          setProject(foundProject);
        } catch (err) {
          setError('Error loading project details');
        } finally {
          setIsLoading(false);
        }
      };
  
      loadProject();
    }, [id]);
  const handleBackClick = () => {
    navigate('/#portfolio');
  };
  
  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <ProjectDetailsSection
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      id="projectdetail"
    >
      <BackButton 
         onClick={handleBackClick}
         whileHover={{ x: -5, background: "#2a9d8f" }}
         whileTap={{ scale: 0.95 }}
      >
        ← Back
      </BackButton>

      <DetailsContainer>
        <ProjectHeader
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1>{project.title}</h1>
          <p className="subtitle">{project.subtitle}</p>
        </ProjectHeader>

        <ProjectImage
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <img src={project.image} alt={project.title} />
        </ProjectImage>

        <ContentSection>
          <MainContent>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <h2>Overview</h2>
              <p>{project.overview}</p>

              <h2>The Challenge</h2>
              <p>{project.challenge}</p>

              <h2>Our Solution</h2>
              <p>{project.solution}</p>

              <Gallery>
                {project.gallery.map((image, index) => (
                  <motion.div
                    key={index}
                    className="image-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    <img src={image} alt={`Project view ${index + 1}`} />
                  </motion.div>
                ))}
              </Gallery>
            </motion.div>
          </MainContent>

          <Sidebar>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <TechList>
                <h3>Technology Stack</h3>
                {Object.entries(project.techStack).map(([category, items]) => (
                  <div key={category}>
                    <h4>{category.toUpperCase()}</h4>
                    <ul>
                      {items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </TechList>

             
            </motion.div>
          </Sidebar>
        </ContentSection>
      </DetailsContainer>
    </ProjectDetailsSection>
  );
};

export default ProjectDetails;